"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hrEmployeeApi, hrEmployerApi } from "@/lib/api/hr";
import { getHrEmployeeSession, getHrEmployerSession } from "@/lib/auth/storage";

export function useEmployeeDashboard() {
  const session = getHrEmployeeSession();
  return useQuery({
    queryKey: ["hr", "employee", "dashboard", session?.employee.id],
    queryFn: () => hrEmployeeApi.dashboard(),
    enabled: !!session,
  });
}

export function useEmployeeLeave() {
  const session = getHrEmployeeSession();
  return useQuery({
    queryKey: ["hr", "employee", "leave", session?.employee.id],
    queryFn: () => hrEmployeeApi.leave(),
    enabled: !!session,
  });
}

export function useEmployeeHours() {
  const session = getHrEmployeeSession();
  return useQuery({
    queryKey: ["hr", "employee", "hours", session?.employee.id],
    queryFn: () => hrEmployeeApi.hours(),
    enabled: !!session,
  });
}

export function useEmployeeTasks() {
  const session = getHrEmployeeSession();
  return useQuery({
    queryKey: ["hr", "employee", "tasks", session?.employee.id],
    queryFn: () => hrEmployeeApi.tasks(),
    enabled: !!session,
  });
}

export function useEmployeePerformance() {
  const session = getHrEmployeeSession();
  return useQuery({
    queryKey: ["hr", "employee", "performance", session?.employee.id],
    queryFn: () => hrEmployeeApi.performance(),
    enabled: !!session,
  });
}

export function useEmployeeNotifications() {
  const session = getHrEmployeeSession();
  return useQuery({
    queryKey: ["hr", "employee", "notifications", session?.employee.id],
    queryFn: () => hrEmployeeApi.notifications(),
    enabled: !!session,
  });
}

export function useHrInvalidate() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["hr"] });
}

export function useEmployerDashboard() {
  const session = getHrEmployerSession();
  return useQuery({
    queryKey: ["hr", "employer", "dashboard"],
    queryFn: () => hrEmployerApi.dashboard(),
    enabled: !!session,
  });
}

export function useEmployerEmployees() {
  const session = getHrEmployerSession();
  return useQuery({
    queryKey: ["hr", "employer", "employees"],
    queryFn: () => hrEmployerApi.employees(),
    enabled: !!session,
  });
}

export function useEmployerLeaveRequests() {
  const session = getHrEmployerSession();
  return useQuery({
    queryKey: ["hr", "employer", "leave"],
    queryFn: () => hrEmployerApi.leaveRequests(),
    enabled: !!session,
  });
}

export function useCreateEmployee() {
  const invalidate = useHrInvalidate();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => hrEmployerApi.createEmployee(body),
    onSuccess: invalidate,
  });
}
